import "./weld-flags";

import { useLDClient } from "launchdarkly-react-client-sdk";
import { useCallback } from "react";

import * as Sentry from "@sentry/react";

import {
  noteLaunchDarklyIdentify,
  noteLaunchDarklyIdentifySkipped,
} from "./identifyCount";

type UserContext = {
  key: string;
  email?: string;
  accountId?: string;
  slug?: string;
};

/**
 * Identical identify calls share one promise: UserProvider identifies with the
 * full user+account context as soon as the user query resolves, and
 * AccountValidator awaits the same context a render later — deduping saves a
 * LaunchDarkly round trip that otherwise blocks the route tree on every load.
 */
let lastIdentify: { key: string; promise: Promise<unknown> } | null = null;

export function useFeatureFlagContext() {
  const ldClient = useLDClient();
  const updateContext = useCallback(
    async (context: UserContext) => {
      if (!ldClient) {
        noteLaunchDarklyIdentifySkipped("client-not-ready");
        return;
      }
      const key = JSON.stringify(context, Object.keys(context).sort());
      if (lastIdentify?.key === key) {
        return lastIdentify.promise;
      }
      const promise = (async () => {
        await ldClient.waitForInitialization(5);
        noteLaunchDarklyIdentify();
        return await ldClient.identify({
          kind: "user",
          ...context,
        });
      })().catch((error) => {
        // Reported even though the caller carries on: a skipped `identify`
        // leaves the flags evaluated against the anonymous context this
        // provider was initialised with, so targeting and percentage rollouts
        // silently resolve to the wrong values for the rest of the session.
        // Only the SDK's own unawaited flag fetch is filtered, in
        // monitoring/sentry.ts.
        Sentry.captureException(error);
        noteLaunchDarklyIdentifySkipped("identify-failed");
        // Let the next caller retry instead of reusing a failed identify.
        if (lastIdentify?.promise === promise) {
          lastIdentify = null;
        }
      });
      lastIdentify = { key, promise };
      return promise;
    },
    [ldClient],
  );
  return {
    updateContext,
    // `useLDClient` is undefined until the SDK provider has finished its own
    // anonymous initialisation; before then `updateContext` can only no-op.
    clientReady: ldClient != null,
  };
}
